import {
  signOut,
  signInWithCustomToken,
  updateProfile,
  onAuthStateChanged as fbOnAuthStateChanged,
  User,
  OAuthProvider,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config';
import { OWNER_EMAILS, clearPlanCache } from './subscription';

export const microsoftProvider = new OAuthProvider('microsoft.com');

// ─── Graph token helpers ───────────────────────────────────────────────────────
export const getGraphToken = () => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('marginpro_graph_token');
};

// ─── User Profile ──────────────────────────────────────────────────────────────
export const ensureUserProfile = async (
  user: User,
  tid: string | null = null,
  _graphToken: string | null = null, // kept for call-site compatibility; no longer stored
  emailOverride?: string,
  nameOverride?: string
): Promise<Record<string, any>> => {
  try {
    if (!db) throw new Error("Firestore not initialized.");
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    const resolvedEmailValue = emailOverride || user.email || "";

    let profileData: any = {
      displayName: nameOverride || user.displayName || "",
      updatedAt: serverTimestamp(),
    };

    // Only write `email` when we actually have one. Background auth-state callers
    // (App.tsx, MarginMenu) pass no override, and custom-token users have a null
    // user.email — writing "" here would create blank-email profiles and, on merge,
    // OVERWRITE a previously-stored email with "". That race is exactly what left
    // Microsoft reviewer accounts with blank/unrecognized emails. Preserve existing.
    if (resolvedEmailValue) profileData.email = resolvedEmailValue;

    if (tid) {
      profileData.tenantId = tid;
    } else if (userSnap.exists() && userSnap.data()?.tenantId) {
      profileData.tenantId = userSnap.data().tenantId;
    }

    // NOTE: Graph tokens are NOT stored in Firestore — they are live OAuth
    // credentials and must not be persisted in a database (SOC2 / security).
    // A fresh token is obtained via SSO on every session.

    // NOTE: plan, role, and subscriptionStatus are NEVER written from client-side.
    // They are protected fields (Firestore rules) set exclusively by Cloud Functions
    // via admin SDK. onUserProfileCreated handles initial provisioning server-side.

    const resolvedEmail = resolvedEmailValue.toLowerCase();

    if (!userSnap.exists()) {
      profileData.createdAt = serverTimestamp();
      profileData.role = resolvedEmail === "john@marginproapp.com" ? "admin" : "Contributor";
      await setDoc(userRef, profileData);
      return { status: "Created", ...profileData };
    } else {
      await setDoc(userRef, profileData, { merge: true });
      return { status: "Exists", ...userSnap.data(), ...profileData };
    }
  } catch (err: any) {
    console.error("Profile sync error:", err);
    throw err;
  }
};

// ─── Office SSO Bootstrap Token ───────────────────────────────────────────────
async function getOfficeBootstrapToken(): Promise<string> {
  if (typeof Office === 'undefined' || !(Office.auth as any)?.getAccessToken) {
    throw new Error(
      "Office API not available. Please open this add-in from within Microsoft Word."
    );
  }
  try {
    return await (Office.auth as any).getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
      // forMSGraphAccess intentionally omitted — personal MSA accounts return 13004
      // when this flag is set because OBO for Graph isn't supported for personal accounts.
      // Graph access is handled separately via getGraphTokenViaDialog() after sign-in.
    });
  } catch (err: any) {
    const code = Number(err?.code ?? err?.errorCode ?? 0);
    if (code === 13001) {
      throw new Error("Please sign in to Microsoft Office first, then try again.");
    }
    if (code === 13003) {
      // Personal MSA accounts may still work — let the error surface naturally
      // rather than blocking them with a hard "not supported" message.
      // Azure AD is configured for Any Entra ID Tenant + Personal Microsoft accounts.
      throw new Error(
        "Sign-in failed. Please make sure you are signed into Microsoft Word, then close and reopen the add-in and try again."
      );
    }
    if (code === 13004) {
      throw new Error(
        "Sign-in failed: this add-in is not yet authorized for your Microsoft account. " +
        "Please close and reopen the add-in and try again. If the problem continues, contact support@marginproapp.com."
      );
    }
    if (code === 13005) {
      // User needs to consent — retry once with an explicit consent prompt
      return await (Office.auth as any).getAccessToken({
        allowConsentPrompt: true,
        allowSignInPrompt: true,
      });
    }
    throw new Error(
      `Office sign-in failed (code ${code}). Please close and reopen the add-in and try again.`
    );
  }
}

// ─── Dialog fallback auth: PKCE → Cloud Function → Firebase ──────────────────
/**
 * Opens an Office dialog running a full PKCE sign-in flow.
 * Used as a fallback when Office SSO fails (e.g. Gmail-linked Microsoft accounts,
 * accounts without M365 subscription, or persistent 13004 errors).
 *
 * The dialog returns a PKCE-issued Graph access token, which is exchanged via
 * exchangeDialogToken (Cloud Function) for a Firebase custom token.
 */
export const loginViaDialog = (): Promise<{ user: User; graphToken: string | null }> => {
  return new Promise((resolve, reject) => {
    if (typeof Office === "undefined" || !Office?.context?.ui) {
      reject(new Error("Office API not available. Please open this add-in inside Microsoft Word."));
      return;
    }

    localStorage.removeItem("marginpro_signin_token_pending");

    const dialogUrl = window.location.origin + "/auth-dialog.html?mode=signin&v=" + Date.now();
    let settled = false;
    let pollInterval: ReturnType<typeof setInterval> | null = null;

    const finish = async (accessToken: string) => {
      if (settled) return;
      settled = true;
      if (pollInterval) clearInterval(pollInterval);
      localStorage.removeItem("marginpro_signin_token_pending");

      try {
        const fns = getFunctions(auth.app, 'us-central1');
        const exchangeToken = httpsCallable(fns, 'exchangeDialogToken');
        const result = await exchangeToken({ accessToken });
        const data = result.data as { customToken: string; graphToken?: string; email: string; name: string };

        const fbResult = await signInWithCustomToken(auth, data.customToken);

        try {
          await updateProfile(fbResult.user, { displayName: data.name || data.email || '' });
        } catch (_) { /* non-fatal */ }

        const graphToken = data.graphToken ?? null;
        if (graphToken) localStorage.setItem('marginpro_graph_token', graphToken);

        await ensureUserProfile(fbResult.user, null, graphToken, data.email, data.name);
        resolve({ user: fbResult.user, graphToken });
      } catch (err: any) {
        const code = err?.code ?? 'unknown';
        const msg  = err?.message ?? 'Sign-in failed. Please try again.';
        reject(new Error(`[${code}] ${msg}`));
      }
    };

    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 65, width: 38 },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(result.error.message));
          return;
        }
        const dialog = result.value;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: any) => {
          try {
            const data = JSON.parse(arg.message);
            if (data.type === "SIGNIN_TOKEN" && data.token) {
              dialog.close();
              finish(data.token);
            }
          } catch (e) {
            console.error("Dialog signin message parse error:", e);
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: any) => {
          if (arg.error === 12006) {
            if (pollInterval) clearInterval(pollInterval);
            if (!settled) reject(new Error("Sign-in cancelled."));
          }
        });

        // Fallback: poll localStorage every 500ms (Mac cross-domain dialog workaround)
        pollInterval = setInterval(() => {
          const raw = localStorage.getItem("marginpro_signin_token_pending");
          if (!raw) return;
          try {
            const data = JSON.parse(raw);
            if (data.type === "SIGNIN_TOKEN" && data.token && Date.now() - data.t < 300_000) {
              dialog.close();
              finish(data.token);
            }
          } catch {
            localStorage.removeItem("marginpro_signin_token_pending");
          }
        }, 500);
      }
    );
  });
};

// ─── Primary auth: Office SSO → Cloud Function → Firebase ─────────────────────
/**
 * Signs in without any navigation, dialog, or browser redirect.
 * Works in Office WKWebView (Mac) and WebView2 (Windows).
 *
 * Flow 1 (preferred): Office.auth.getAccessToken()
 *    → exchangeOfficeToken (Cloud Function: validates, OBO, mints custom token)
 *    → signInWithCustomToken → Firebase user
 *
 * Flow 2 (fallback): SSO fails (13004, Gmail-linked account, etc.)
 *    → loginViaDialog() opens PKCE sign-in dialog
 *    → exchangeDialogToken (Cloud Function: validates Graph token, mints custom token)
 *    → signInWithCustomToken → Firebase user
 */
export const loginWithSSO = async (): Promise<{ user: User; graphToken: string | null }> => {
  // 1. Attempt Office SSO — fast path, no UI for most users
  let bootstrapToken: string;
  try {
    bootstrapToken = await getOfficeBootstrapToken();
  } catch (ssoErr: any) {
    const msg = ssoErr?.message ?? '';
    // Respect explicit user cancellation — don't open a dialog they didn't ask for
    if (msg.toLowerCase().includes('cancel')) throw ssoErr;
    // All other SSO failures → fall back to interactive PKCE dialog
    console.warn('[loginWithSSO] SSO unavailable, falling back to dialog sign-in:', msg);
    return loginViaDialog();
  }

  // 2. Exchange bootstrap token via Cloud Function — validates, OBO, mints custom token
  const fns = getFunctions(auth.app, 'us-central1');
  const exchangeToken = httpsCallable(fns, 'exchangeOfficeToken');
  let data: { customToken: string; graphToken?: string; email: string; name: string };
  try {
    const result = await exchangeToken({ bootstrapToken });
    data = result.data as typeof data;
  } catch (err: any) {
    // Surface the full Firebase error code + message for easier debugging
    const code = err?.code ?? 'unknown';
    const msg = err?.message ?? 'Authentication service error. Please try again.';
    throw new Error(`[${code}] ${msg}`);
  }

  // 3. Sign into Firebase with the minted custom token
  const fbResult = await signInWithCustomToken(auth, data.customToken);

  // 4. Stamp real display name + email onto the Firebase user object.
  //    signInWithCustomToken creates a bare user — email/name must be set explicitly.
  try {
    await updateProfile(fbResult.user, {
      displayName: data.name || data.email || '',
    });
  } catch (_) { /* non-fatal */ }

  // 5. Persist Graph token for OneDrive sync
  const graphToken = data.graphToken ?? null;
  if (graphToken) {
    localStorage.setItem('marginpro_graph_token', graphToken);
  }

  // 6. Write Firestore profile with real email/name from the SSO token
  await ensureUserProfile(fbResult.user, null, graphToken, data.email, data.name);
  return { user: fbResult.user, graphToken };
};

// ─── Graph token via Office Dialog (for personal MSA accounts) ────────────────
/**
 * Opens an Office dialog running a PKCE flow scoped only to Microsoft Graph.
 * Use this when loginWithSSO() returned a null graphToken (personal MSA accounts
 * don't support OBO, so the SSO bootstrap token can't be exchanged for Graph).
 *
 * The user is already signed into Firebase — this only acquires the Graph token
 * needed for OneDrive sync.
 */
export const getGraphTokenViaDialog = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (typeof Office === "undefined" || !Office?.context?.ui) {
      reject(new Error("Office API not available."));
      return;
    }

    // Clear any stale pending token
    localStorage.removeItem("marginpro_graph_token_pending");

    const dialogUrl =
      window.location.origin + "/auth-dialog.html?mode=graph&v=" + Date.now();
    let settled = false;
    let pollInterval: ReturnType<typeof setInterval> | null = null;

    const finish = (token: string) => {
      if (settled) return;
      settled = true;
      if (pollInterval) clearInterval(pollInterval);
      localStorage.removeItem("marginpro_graph_token_pending");
      localStorage.setItem("marginpro_graph_token", token);
      resolve(token);
    };

    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 65, width: 38 },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(result.error.message));
          return;
        }
        const dialog = result.value;

        // PRIMARY: messageParent from dialog
        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (arg: any) => {
            try {
              const data = JSON.parse(arg.message);
              if (data.type === "GRAPH_TOKEN" && data.token) {
                dialog.close();
                finish(data.token);
              }
            } catch (e) {
              console.error("Graph token dialog message parse error:", e);
            }
          }
        );

        // Dialog closed by user
        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          (arg: any) => {
            if (arg.error === 12006) {
              if (pollInterval) clearInterval(pollInterval);
              if (!settled) reject(new Error("OneDrive connection cancelled."));
            }
          }
        );

        // FALLBACK: poll localStorage every 500 ms
        // (used when messageParent is unavailable after cross-domain nav on Mac)
        pollInterval = setInterval(() => {
          const raw = localStorage.getItem("marginpro_graph_token_pending");
          if (!raw) return;
          try {
            const data = JSON.parse(raw);
            if (
              data.type === "GRAPH_TOKEN" &&
              data.token &&
              Date.now() - data.t < 300_000
            ) {
              dialog.close();
              finish(data.token);
            }
          } catch {
            localStorage.removeItem("marginpro_graph_token_pending");
          }
        }, 500);
      }
    );
  });
};

// ─── Standard exports ──────────────────────────────────────────────────────────
export const logout = async () => {
  if (typeof window !== 'undefined') localStorage.removeItem('marginpro_graph_token');
  clearPlanCache();
  return await signOut(auth);
};

export const onAuthStateChanged = (cb: (user: User | null) => void) => {
  if (!auth) { cb(null); return () => {}; }
  return fbOnAuthStateChanged(auth, cb);
};

export const getOneDriveProfile = async (token: string) => {
  const response = await fetch('https://graph.microsoft.com/v1.0/me', {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Failed to fetch OneDrive profile: ${response.status}`);
  return await response.json();
};
